import {BaseService} from './BaseService';
import {MstDepartmentDTOInterface} from '../DTO/MstDepartmentDTOInterface';
import {toCamel, toSnake} from '../Util/SnakeCamelUtil';
import {ReservationDAO} from '../DAO/ReservationDAO';
import {ReservationSaveDTOInterface} from '../DTO/ReservationSaveDTOInterface';
import {ReservationDTOInterface} from '../DTO/ReservationDTOInterface';
import {ReservationListDTOInterface} from '../DTO/ReservationListDTOInterface';
import {ReservationDetailDTOInterface} from '../DTO/ReservationDetailDTOInterface';
import {MstClinicDTOInterface} from '../DTO/MstClinicDTOInterface';
import {MstDoctorDTOInterface} from '../DTO/MstDoctorDTOInterface';
import {UserSearchResultDTOInterface} from '../DTO/UserSearchResultDTOInterface';
import {DateUtil} from '../Util/DateUtil';
import {ReservationFrameDTOInterface} from '../DTO/ReservationFrameDTOInterface';
import {MstExaminationDTOInterface} from '../DTO/MstExaminationDTOInterface';
import {DepartmentListDTOInterface} from '../DTO/DepartmentListDTOInterface';
import {DeviceTypeDTOInterface} from '../DTO/DeviceTypeDTOInterface';
import {ReserveAlertListDTOInterface} from '../DTO/ReserveAlertListDTOInterface';
import {ReservationOnlineAgreementInsureDTOInterface} from '../DTO/ReservationOnlineAgreementInsureDTOInterface';
import {ReservationInterviewDTOInterface} from '../DTO/ReservationInterviewDTOInterface';
import {ReservationUserLimitErrorDTOInterface} from '../DTO/ReservationUserLimitErrorDTOInterface';

export class ReserveService extends BaseService {

	/**
	 * ログインなし予約作成
	 * @param data
	 */
	public async createReservationByAnonymous(data: ReservationSaveDTOInterface): Promise<{token: string}> {
		const dao = new ReservationDAO(this as BaseService);
		const res = await dao.createReservationByAnonymous(data);

		return res;
	}

	/**
	 * ログインユーザーによる予約作成
	 * @param data
	 * @param onlineAgreement
	 */
	public async createReservationByUser(
		data: ReservationSaveDTOInterface,
		onlineAgreement: ReservationOnlineAgreementInsureDTOInterface
		): Promise<ReservationDetailDTOInterface|ReservationUserLimitErrorDTOInterface> {
		const dao = new ReservationDAO(this as BaseService);
		const res = await dao.createReservationByUser(data, onlineAgreement);

		return res;
	}

	/**
	 * 予約更新
	 * @param data
	 * @param onlineAgreement
	 */
	public async updateReservation(
		data: ReservationSaveDTOInterface,
		onlineAgreement: ReservationOnlineAgreementInsureDTOInterface
	): Promise<ReservationDetailDTOInterface|ReservationUserLimitErrorDTOInterface> {
		const dao = new ReservationDAO(this as BaseService);
		const res = await dao.updateReservation(data, onlineAgreement);

		return res;
	}

	/**
	 * ログインユーザーの予約一覧の取得
	 * 現在と過去を分けて取得
	 */
	public async getReservationList(): Promise<{
		valid: Array<ReservationListDTOInterface>;
		inValid: Array<ReservationListDTOInterface>;
	}> {
		const dao = new ReservationDAO(this as BaseService);
		const res = await dao.getReservationList();
		const data = {
			valid:  this.obj2Array(toCamel(res.valid)),
			inValid: this.obj2Array(toCamel(res.in_valid)),
		};

		return data;
	}

	/**
	 * 予約詳細画面のデータ取得
	 * @param reservationId
	 */
	public async getReserveDetailViewData(reservationId: number): Promise<{
		reservation: ReservationDetailDTOInterface;
		updateDeadline: string;
		cancelDeadline: string;
	}> {
		this.log('ReserveService::getReserveDetailViewData()');
		const dao = new ReservationDAO(this as BaseService);
		const ret = await dao.getReserveDetailViewData(reservationId);

		const reserve = ret.reservation;
		const reservation: ReservationDetailDTOInterface = {
			reservation: toCamel(reserve.reservation) as ReservationDTOInterface,
			user: toCamel(reserve.user) as UserSearchResultDTOInterface,
			mstClinic: toCamel(reserve.mst_clinic) as MstClinicDTOInterface,
			mstDepartment: toCamel(reserve.mst_department) as MstDepartmentDTOInterface,
			mstDoctor: toCamel(reserve.mst_doctor) as MstDoctorDTOInterface,
			mstExamination: this.obj2Array(toCamel(reserve.mst_examination)) as Array<MstExaminationDTOInterface>,
			interview: this.obj2Array(toCamel(reserve.interview)) as Array<ReservationInterviewDTOInterface>
		};

		this.log(reservation);

		return {
			reservation,
			updateDeadline: ret.update_deadline,
			cancelDeadline: ret.cancel_deadline
		};
	}

	/**
	 * ログイン前に一時保存している仮予約データの取得
	 * @param token
	 */
	public async getTmpReserve(token: string): Promise<ReservationSaveDTOInterface> {
		const dao = new ReservationDAO(this as BaseService);
		const ret = await dao.getTmpReserve(token);

		const examList = this.obj2Array(toCamel(ret.exam_list));
		const reserve =  toCamel(ret) as ReservationSaveDTOInterface;
		reserve.examList = examList;

		return reserve;
	}

	/**
	 * 一時保存予約の削除
	 * @param token
	 */
	public async deleteTmpReserve(token: string): Promise<any> {
		const dao = new ReservationDAO(this as BaseService);
		return await dao.deleteTmpReserve(token);
	}

	/**
	 * 予約キャンセル
	 * @param param
	 */
	public async cancelReservation(param: { data: ReservationDTOInterface }): Promise<ReservationDTOInterface> {
		const dao = new ReservationDAO(this as BaseService);
		const res = await dao.cancelReservation({
			reservationId: param.data.id
		});

		return res;
	}

	/**
	 * 予約更新画面 初期データ取得
	 * @param param
	 */
	public async getUpdateViewData(param: { reservationId: number }): Promise<{
		reservation: ReservationDetailDTOInterface;
		selectedClinicId: number;
		clinicList: Array<MstClinicDTOInterface>;
		departmentVisitList: Array<MstDepartmentDTOInterface>;
		departmentOnlineList: Array<MstDepartmentDTOInterface>;
		visitTypeList: Array<{id: number; name: string}>;
		appointmentTypeList: Array<{id: number; name: string}>;
		deviceTypeList: Array<DeviceTypeDTOInterface>;
		alertList: ReserveAlertListDTOInterface;
	}> {
		const dao = new ReservationDAO(this as BaseService);
		const res = await dao.getUpdateViewData(param);

		return res;
	}


	/**
	 * 新規予約画面初期データ取得
	 * @param clinicId
	 */
	public async getReserveStartViewData(clinicId: number): Promise<{
		selectedClinicId: number;
		clinicList: Array<MstClinicDTOInterface>;
		departmentVisitList: Array<DepartmentListDTOInterface>;
		departmentOnlineList: Array<DepartmentListDTOInterface>;
		visitTypeList: Array<{id: number; name: string}>;
		appointmentTypeList: Array<{id: number; name: string}>;
		deviceTypeList: Array<DeviceTypeDTOInterface>;
		alertList: ReserveAlertListDTOInterface;
	}> {
		const dao = new ReservationDAO(this as BaseService);
		const res = await dao.getReserveStartViewData(clinicId);

		return res;
	}


	/**
	 * 予約で必要な規約の取得
	 * @param reserve
	 */
	public async getAgreement(reserve: ReservationSaveDTOInterface): Promise<any> {
		const dao = new ReservationDAO(this as BaseService);
		const res = await dao.getAgreement(reserve);

		return res;
	}


	/**
	 * 予約日時選択画面のデータ取得
	 * @param param
	 */
	public async getReserveSelectDateViewData(param: {
		reserve: ReservationSaveDTOInterface;
		startDate: Date;
		count: number;//取得日数
	}): Promise<{
		data: ReservationFrameDTOInterface;
		doctorList: Array<MstDoctorDTOInterface>;
	}> {
		const dao = new ReservationDAO(this as BaseService);

		// 14日間のデータ取得
		const fromDate = DateUtil.getDateString(param.startDate);
		const toDate = new Date(param.startDate);
		toDate.setDate(toDate.getDate() + (param.count - 1));

		const res = await dao.getReserveSelectDateViewData({
			fromDate,
			toDate: DateUtil.getDateString(toDate),
			doctorId: param.reserve.doctorId,
			departmentId: param.reserve.departmentId,
			appointmentType: param.reserve.appointmentType,
			visitType: param.reserve.visitType,
			examList: param.reserve.examList
		});

		return res;
	}

	/**
	 * 予約完了メッセージの送信
	 * @param reserveId
	 */
	public async sendReserveCompleteMessage(reserveId: number): Promise<ReservationDetailDTOInterface> {
		const dao = new ReservationDAO(this as BaseService);
		const res = await dao.sendReserveCompleteMessage(reserveId);

		return res;
	}

	/**
	 * 予約変更メッセージの送信
	 * @param reserveId
	 */
	public async sendReserveUpdateMessage(reserveId: number): Promise<ReservationDetailDTOInterface> {
		const dao = new ReservationDAO(this as BaseService);
		const res = await dao.sendReserveUpdateMessage(reserveId);

		return res;
	}

	/**
	 * 予約キャンセルメッセージの送信
	 * @param reserveId
	 */
	public async sendReserveCancelMessage(reserveId: number): Promise<ReservationDetailDTOInterface> {
		const dao = new ReservationDAO(this as BaseService);
		const res = await dao.sendReserveCancelMessage(reserveId);

		return res;
	}

	/**
	 * 同意書への同意
	 * @param token
	 */
	public async agreementNoneInsure(token: string): Promise<ReservationDetailDTOInterface> {
		const dao = new ReservationDAO(this as BaseService);
		const res = await dao.agreementNoneInsure(token);

		return res;
	}

	/**
	 * 保険外負担に関する同意書のデータ取得
	 * @param token
	 */
	public async getOnlineAgreementInsure(token: string): Promise<any> {
		const dao = new ReservationDAO(this as BaseService);
		const res = await dao.getOnlineAgreementInsure(token);

		return toCamel(res);
	}
}
